import React, { useEffect, useState } from 'react';
import { Spinner, useStateContext } from '@veda/dashboard-ui';
import { usePppApi } from '../services/pppApi';
import { useAuthUser } from '../hooks/useAuthUser';
import { useDownload } from '../hooks/useDownload';

function KpiCard({ label, value, color }) {
  const colors = {
    blue:   'bg-blue-50 dark:bg-blue-900/20 text-blue-700',
    green:  'bg-green-50 dark:bg-green-900/20 text-green-700',
    red:    'bg-red-50 dark:bg-red-900/20 text-red-700',
    orange: 'bg-orange-50 dark:bg-orange-900/20 text-orange-700',
  };
  return (
    <div className={`rounded-xl p-4 text-center ${colors[color]}`}>
      <div className="text-3xl font-bold">{value}</div>
      <div className="text-xs mt-1 opacity-80">{label}</div>
    </div>
  );
}

export default function ReportPerformanceRup() {
  const { _locate } = useStateContext();
  const api = usePppApi();
  const { user, can } = useAuthUser();
  const { download, downloading } = useDownload();
  const [utentiRup, setUtentiRup] = useState([]);
  const [selectedRupId, setSelectedRupId] = useState('');
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingUtenti, setLoadingUtenti] = useState(true);

  const isAdmin = can('ppp.view.all');

  useEffect(() => {
    if (isAdmin) {
      api.listUtentiRup({
        onSuccess: (data) => { setUtentiRup(data.data ?? data); setLoadingUtenti(false); },
        onFormError: () => setLoadingUtenti(false),
      });
    } else {
      setLoadingUtenti(false);
      if (user?.id) setSelectedRupId(String(user.id));
    }
  }, [isAdmin, user]); // eslint-disable-line react-hooks/exhaustive-deps

  const carica = () => {
    const rupId = selectedRupId || (user?.id ? String(user.id) : '');
    if (!rupId) return;
    setLoading(true);
    setReport(null);
    api.getReportPerformanceRup(rupId, {
      onSuccess: (data) => { setReport(data); setLoading(false); },
      onFormError: () => setLoading(false),
    });
  };

  return (
    <div className="p-6">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6 mb-6">
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
          {_locate('ppp_report_performance_rup_titolo')}
        </h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          {_locate('ppp_report_performance_rup_desc')}
        </p>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 mb-6 flex flex-wrap gap-3 items-end">
        {isAdmin && (
          <div className="flex-1 min-w-[220px]">
            <label className="block text-xs font-medium text-gray-500 mb-1">RUP</label>
            {loadingUtenti ? <Spinner /> : (
              <select
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm dark:bg-gray-700 dark:text-white"
                value={selectedRupId}
                onChange={(e) => { setSelectedRupId(e.target.value); setReport(null); }}
              >
                <option value="">{_locate('ppp_report_scegli_rup')}</option>
                {utentiRup.map((u) => (
                  <option key={u.id} value={u.id}>{u.name}</option>
                ))}
              </select>
            )}
          </div>
        )}
        <button
          onClick={carica}
          disabled={loading || (isAdmin && !selectedRupId)}
          className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-50 transition-colors"
        >
          {loading ? _locate('ppp_report_caricamento') : _locate('ppp_report_genera')}
        </button>
        {report && (
          <button
            onClick={() => download(api.urlReportPerformanceRupPdf(selectedRupId), `report_performance_rup_${selectedRupId || 'me'}.pdf`)}
            disabled={downloading}
            className="px-4 py-2 bg-gray-700 text-white rounded-lg text-sm font-medium hover:bg-gray-800 disabled:opacity-50 transition-colors"
          >
            {downloading ? '...' : 'PDF'}
          </button>
        )}
      </div>

      {loading && <div className="flex justify-center items-center h-40"><Spinner /></div>}

      {report && (
        <div className="space-y-6">
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
            <h2 className="font-semibold text-gray-900 dark:text-white mb-4">{report.rupNome}</h2>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <KpiCard label={_locate('ppp_report_perf_assegnati')} value={report.procedimentiAssegnati} color="blue" />
              <KpiCard label={_locate('ppp_report_perf_in_corso')} value={report.procedimentiInCorso} color="blue" />
              <KpiCard label={_locate('ppp_report_perf_conclusi_ok')} value={report.procedimentiConclusiNeiTermini} color="green" />
              <KpiCard label={_locate('ppp_report_perf_ritardi')} value={report.totaleRitardi} color="red" />
              <KpiCard label={_locate('ppp_report_perf_proroghe')} value={report.totaleProrogheMotivaate} color="orange" />
            </div>
          </div>

          {report.ritardi.length > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-700">
                <h3 className="font-semibold text-red-700">{_locate('ppp_report_perf_sezione_ritardi')}</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-sm">
                  <thead className="bg-gray-50 dark:bg-gray-900">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">{_locate('ppp_report_col_procedimento')}</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">{_locate('ppp_report_col_scadenza')}</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">{_locate('ppp_report_col_data')}</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase">{_locate('ppp_report_col_ritardo')}</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase hidden lg:table-cell">{_locate('ppp_report_col_normativa')}</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-100 dark:divide-gray-700">
                    {report.ritardi.map((r, i) => (
                      <tr key={i} className="hover:bg-gray-50 dark:hover:bg-gray-750">
                        <td className="px-4 py-3">
                          <div className="font-medium text-gray-900 dark:text-white">{r.titoloProcedimento}</div>
                          {r.codiceProcedimento && <div className="text-xs text-gray-400 mt-0.5">{r.codiceProcedimento}</div>}
                        </td>
                        <td className="px-4 py-3 text-gray-600 dark:text-gray-300">{r.descrizioneScadenza}</td>
                        <td className="px-4 py-3 text-gray-600 dark:text-gray-300 whitespace-nowrap">
                          {new Date(r.dataScadenza).toLocaleDateString('it-IT')}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <span className="inline-flex items-center justify-center rounded-full bg-red-100 text-red-700 font-bold px-2.5 py-0.5 text-xs min-w-[3rem]">
                            +{r.giorniRitardo}gg
                          </span>
                        </td>
                        <td className="px-4 py-3 text-gray-400 text-xs italic hidden lg:table-cell">{r.riferimentoNormativo}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {report.proroghe.length > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-700">
                <h3 className="font-semibold text-orange-700">{_locate('ppp_report_perf_sezione_proroghe')}</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-sm">
                  <thead className="bg-gray-50 dark:bg-gray-900">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">{_locate('ppp_report_col_procedimento')}</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">{_locate('ppp_report_col_scadenza')}</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">{_locate('ppp_report_termini_col_normativa')}</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">{_locate('ppp_report_termini_col_effettiva')}</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase">+Giorni</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">{_locate('ppp_report_motivazione_proroga')}</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-100 dark:divide-gray-700">
                    {report.proroghe.map((p, i) => (
                      <tr key={i} className="hover:bg-gray-50 dark:hover:bg-gray-750">
                        <td className="px-4 py-3">
                          <div className="font-medium text-gray-900 dark:text-white">{p.titoloProcedimento}</div>
                          {p.codiceProcedimento && <div className="text-xs text-gray-400 mt-0.5">{p.codiceProcedimento}</div>}
                        </td>
                        <td className="px-4 py-3 text-gray-600 dark:text-gray-300">{p.descrizioneScadenza}</td>
                        <td className="px-4 py-3 text-gray-600 dark:text-gray-300 whitespace-nowrap">
                          {new Date(p.dataScadenzaOriginale).toLocaleDateString('it-IT')}
                        </td>
                        <td className="px-4 py-3 text-gray-600 dark:text-gray-300 whitespace-nowrap">
                          {new Date(p.dataScadenzaProrogata).toLocaleDateString('it-IT')}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className="text-orange-600 font-semibold">+{p.giorniProroga}gg</span>
                        </td>
                        <td className="px-4 py-3 text-gray-500 dark:text-gray-400 text-xs">{p.motivazione ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
